import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";

import { Button } from 'react-bootstrap';
import Icon from '../common/components/Icon';
import TypeaheadInput from "../common/components/TypeaheadInput";
import useClientConfig from "../common/hooks/clientConfigHook";
import useNetwork from '../common/hooks/networkHook';
import { useLocationsForSiteLevelType } from "../reducers/locationSlice";
import { altCoord, geocode, geovalidate } from '../utils/geoUtilities';
import PropTypes from 'prop-types';
import { Col, Row } from '../common/components/Grid';
import CustomForm from '../common/components/CustomForm';
import AddressInput, { toString as addressToString } from '../common/components/AddressInput';
import useMessageDisplay from '../common/components/MessageDisplay';
import { ReactNodeDialog } from '../common/utils/dialogUtlities';
import Select from '../common/components/Select';
import ReactForm from '../common/components/ReactForm';
import { deepMerge } from '../common/utils/objectUtilities';

const ValidationSelectionDialog = ({ results, onClose }) => {
    const [options, setOptions] = useState(results?.map((r, id) => ({ ...r, id })));
    const [selectedOption, setSelectedOption] = useState('0');
    const formRef = useRef();

    useEffect(() => {
        setOptions(results?.map((r, id) => ({ ...r, id })) ?? [])
        setSelectedOption('0');
    }, [results]);

    const handleSave = useCallback(() => {
        if (formRef.current?.checkValidity()) {
            onClose(results[selectedOption]);
        }
    }, [onClose, selectedOption, results])

    return <ReactNodeDialog showModal={options?.length > 0} onClose={() => onClose()} onSave={handleSave}>
        <ReactForm ref={formRef}>
            <Select value={selectedOption} options={options} labelField="location" valueField="id" onChange={setSelectedOption} required></Select>
        </ReactForm>
    </ReactNodeDialog>
}

const LocationInput = ({ id, site, type, level, location, details, mode = 'simple', placeholder, disabled = false, disableGeocode = false, required = false, onChange, live = false }) => {
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const { t } = useTranslation();
    const { showError } = useMessageDisplay();
    const locations = useLocationsForSiteLevelType(site, level, type);
    const [inverseDelta, setInverseDelta] = useState();
    const optValueComparator = useCallback((opt, idx, v) => 
        opt.name === v 
        && (!clientConfig.enableSites || !opt.site || opt.site === site)
        && (!clientConfig.enableLevels || !opt.level || opt.level === level)
        && (!opt.type || opt.type === type)
    , [clientConfig, site, level, type]);
    const [validationOptions, setValidationOptions] = useState();

    const renderLocation = useCallback((l, idx) => {
        let label = l.name;
        let suffix = [];
        if (clientConfig.enableGrid && l.gridx && l.gridy) {
            label += ` @ [${l.gridx},${l.gridy}]`;
        }
        if (clientConfig.enableSites && l.site && l.site !== site) {
            suffix.push(l.site)
        }
        if (clientConfig.enableLevels && l.level && l.level !== level) {
            suffix.push(l.level)
        }
        if (l.type && l.type !== type) {
            suffix.push(l.type)
        }
        if (suffix.length > 0) {
            label += ` [${suffix.join(' - ')}]`;
        }
        return label;
    }, [clientConfig, site, level, type])
    
    const selectValue = useCallback((l, id) => id, []);
    
    const handleSimpleUpdate = useCallback((selection, delta = {}) => {
        let update = delta;
        if (selection?.customOption) {
            update.location = selection.label;
        } else if (selection?.id != null) {
            let l = locations[selection.id];
            update.location = l.name;
            if (l.site && l.site !== site) {
                update.site = l.site;
            }
            if (l.level && l.level !== level) {
                update.level = l.level;
            }
            if (l.type && l.type !== type) {
                update.type = l.type;
            }
            if (l.lat && l.long) {
                update.latitude = l.lat;
                update.longitude = l.long;
            } else {
                update.latitude = null;
                update.longitude = null;
            }
            if (l.w3w) {
                update.w3w = l.w3w;
            }
            if (clientConfig.enableGrid && l.gridx && l.gridy) {
                update.gridx = l.gridx;
                update.gridy = l.gridy;
            }
        } else {
            update.location = selection;
        }
        if (inverseDelta) {
            deepMerge(update, inverseDelta);
            setInverseDelta();
        }
        onChange(update);
    }, [clientConfig.enableGrid, locations, inverseDelta, site, level, type, onChange]);

    const handleAddressUpdate = useCallback(async (field, value) => {
        const delta = { details: { [field]: value } };
        const location = await addressToString({ value: { ...details, [field]: value }, defaultValue: '', clientConfig });
        handleSimpleUpdate(location, delta);
    }, [clientConfig, details, handleSimpleUpdate]);

    const doGeocode = async () => {
        try {
            const pos = await geocode(network, location);
            if (pos) {
                let delta = { latitude: pos.lat, longitude: pos.lon };
                if (clientConfig.altcoordMode !== 'none') {
                    if (pos.w3w) {
                        delta.w3w = pos.w3w;
                    } else {
                        let res = await altCoord(network, pos.lat, pos.lon)
                        if (res?.addr) {
                            delta.w3w = res.addr;
                        }
                    }
                }
                onChange(delta);
            }
        } catch (err) {
            console.log("Error with geocoding ", err);
        }
    }

    const processValidation = useCallback(validation => {
        if (validation) {
            const { delta, inverse, location, ...props } = validation;
            setInverseDelta(inverse);
            switch (mode) {
                case 'simple':
                    onChange({ ...delta, location })
                    break;
                case 'address':
                    onChange({ ...delta, location, details: { address1: null, address2: null, suburb: null, state: null, postcode: null, ...props } });
                    break;
                default:
                    throw new Error("Unknown address mode for validation " + mode);
            }
        }
        setValidationOptions();
    }, [onChange, mode]);

    const doValidate = async () => {
        try {
            let payload;
            switch (mode) {
                case 'simple':
                    payload = location;
                    break;
                case 'address':
                    payload = details;
                    break;
                default:
                    throw new Error("Unknown location input mode " + mode);
            }
            const validation = await geovalidate(network, payload);
            if (validation.length === 1) {
                processValidation(validation[0]);
            } else if (validation.length > 1) {
                setValidationOptions(validation)
            } else {
                showError(t('geovalidationFailed'));
            }
        } catch (err) {
            showError(t('geovalidationFailed'));
            console.log("Error with geovalidation ", err);
        }
    }

    const geocodeButton = clientConfig.enableGeocoding && !disabled && !disableGeocode && <Button className="h-100" variant="outline-dark" disabled={!location} onClick={doGeocode}><Icon variant="solid" kind="crosshairs"></Icon></Button>
    const validateButton = clientConfig.enableGeovalidation && !disabled && <Button className="h-100" variant="outline-dark" disabled={!location} onClick={doValidate}><Icon variant="solid" kind="check-to-slot"></Icon></Button>

    const selectedLocation = locations?.find(l => l.name === location);

    let locationCtrl;
    switch (mode) {
        case 'simple':
            locationCtrl = <TypeaheadInput id={id} live={live} options={locations} value={location} placeholder={(placeholder ?? t('location', { count: 1 }))} required={required} disabled={disabled} lockdown={false} 
                optLabel={renderLocation} optValue={selectValue} optValueComparator={optValueComparator} onChange={handleSimpleUpdate} appendCtrl={<>{geocodeButton}{validateButton}</>}></TypeaheadInput>;
            break;
        case 'address':
            locationCtrl = <Row id={id}>
                <Col xs>
                    <AddressInput id="locationInput" address={details} placeholder={placeholder} disabled={disabled} required={required} live={live} onChange={handleAddressUpdate}></AddressInput>
                </Col>
                { geocodeButton && <Col className="pt-3" xs="auto">
                    {geocodeButton}
                </Col> }
                { validateButton && <Col className="pt-3" xs="auto">
                    {validateButton}
                </Col> }
            </Row>;
            break;
        default:
            throw new Error("Unknown location input mode " + mode);
    }

    return <>
        <ValidationSelectionDialog results={validationOptions} onClose={processValidation}></ValidationSelectionDialog>
        <Row>
            <Col>
                {locationCtrl}                
            </Col>
        </Row>
        { clientConfig.locationDescriptor && selectedLocation && <Row className="my-0">
            <Col className="px-2 fieldLabel">
                <CustomForm def={clientConfig.locationDescriptor} data={selectedLocation} readonly></CustomForm>
            </Col>
        </Row> }
    </>
}

LocationInput.propTypes = {
    site: PropTypes.string,
    level: PropTypes.string,
    type: PropTypes.string,
    location: PropTypes.string,
    details: PropTypes.object,
    mode: PropTypes.oneOf(['simple','address']),
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    disableGeocode: PropTypes.bool,
    live: PropTypes.bool,
    onChange: PropTypes.func
}

export default LocationInput;